/**
 * @fileoverview
 * Core utility functions for string manipulation, CSS class management, and data extraction.
 */

import { AccountInfo } from '@azure/msal-browser';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const USER_ID_PREFIX = 'employee_id://';

/**
 * Combines and merges CSS class names using clsx and tailwind-merge.
 *
 * @param {...ClassValue[]} inputs - CSS class values to combine and merge
 * @returns {string} Returns merged CSS class string
 */
export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

/**
 * Truncates a string if it exceeds the specified maximum length.
 *
 * @param {string} str - The string to potentially truncate
 * @param {number} maxLength - Maximum allowed length before truncation
 * @returns {string} Returns truncated string with ellipsis or original string if within limit
 */
export const truncateStringLargerThan = (str: string, maxLength: number) => {
  if (str.length > maxLength) {
    return `${str.substring(0, maxLength)}...`;
  }
  return str;
};

/**
 * Extracts user ID from Azure MSAL account information.
 *
 * @param {AccountInfo} account - Azure MSAL account object containing ID token claims
 * @returns {string} Returns formatted ID string or empty string if no valid ID found
 */
export const extractIdFromAccount = (account: AccountInfo): string => {
  if (account.idTokenClaims) {
    if (account.idTokenClaims.employee_id) {
      return `${USER_ID_PREFIX}${account.idTokenClaims.employee_id}`;
    }
    if (account.idTokenClaims.sub) {
      return `sub://${account.idTokenClaims.sub}`;
    }
  }
  return '';
};

/**
 * Converts a string to kebab-case format.
 *
 * @param {string} str - The string to convert to kebab-case
 * @returns {string} Returns kebab-case formatted string
 */
export const toKebabCase = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Generates a unique key by merging an array of strings with hash computation.
 *
 * @param {(string | null | undefined)[]} parts - Array of string parts to merge into a unique key
 * @returns {string} Returns unique key string derived from the input parts
 */
export function uniqueKey(parts: (string | null | undefined)[]): string {
  // Replace null or undefined values with a specific placeholder.
  const normalizedParts = parts.map((part) => (part == null ? '<undefined>' : part));

  // Concatenate the parts with a delimiter.
  const concatenated = normalizedParts.join('::');

  // Compute a simple hash from the concatenated string.
  let hash = 0;
  for (let i = 0; i < concatenated.length; i++) {
    const char = concatenated.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32-bit integer.
  }

  // Return the key as a string (using base-36 encoding for brevity).
  return 'key_' + Math.abs(hash).toString(36);
}

/**
 * Capitalizes the first letter of a string and lowercases the rest.
 *
 * @param {string} str - The string to capitalize
 * @returns {string} Returns string with first letter capitalized and remaining letters lowercase
 */
export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
